/**
 * ComposerPage — create and publish one post to many networks.
 *
 * Layout:
 *   ┌─────────┬──────────────────────────────────────────────────────┐
 *   │ Media   │ ☑ Facebook ☑ YouTube ☐ Instagram … (one per account) │
 *   │ library ├───────────────┬──────────────────┬─────────┬────────┤
 *   │ (folder │ Compose Your  │ Platform         │[Preview]│ Publish│
 *   │  tree,  │ Post          │ Settings         │         │        │
 *   │  colla- ├───────────────┴──────────────────┼─────────┴────────┤
 *   │  psible)│  the active tab fills this area: │ summary          │
 *   │         │  editor + media, or the settings │ Schedule         │
 *   │         │  for each selected platform      │ Publish          │
 *   └─────────┴──────────────────────────────────┴──────────────────┘
 *
 * Preview is a button, not a pane: checking it is a moment, not a mode, so it
 * opens over the top (see `PreviewDialog`) rather than holding width open on
 * every other screen.
 *
 * The library is its own full-height rail rather than a panel inside the
 * workspace: picking an existing asset is something you do *while* writing, so
 * it stays visible whichever tab is open.
 *
 * The destination strip is at the top because everything below reads from it:
 * the character counters, the preview and the settings tab all render per
 * selected platform.
 *
 * The rail, the workspace and the publish pane each scroll on their own, so a
 * long settings list can never push the publish button out of view.
 *
 * Below `lg` everything stacks and the page scrolls as one. It is all fed by
 * `useComposer`, which is mock-backed until the publishing API exists.
 */

import { useCallback, useRef, useState } from 'react';
import { ChevronDown, Eye, FileText, Loader2, Save, Send, Sparkles, X } from 'lucide-react';

import { cn } from '../../lib/utils';
import { useTranslation } from '../../context/LanguageContext';
import { usePageHeader } from '../../context/PageHeaderContext';
import { useComposer } from '../../hooks/useComposer';
import { PostEditor } from '../../components/dashboard/composer/PostEditor';
import { MediaUpload } from '../../components/dashboard/composer/MediaUpload';
import type { MediaUploadHandle } from '../../components/dashboard/composer/MediaUpload';
import { MediaLibraryTree } from '../../components/dashboard/composer/MediaLibraryTree';
import { PlatformSelector } from '../../components/dashboard/composer/PlatformSelector';
import { PreviewDialog } from '../../components/dashboard/composer/PreviewDialog';
import { ScheduleSection } from '../../components/dashboard/composer/ScheduleSection';
import { PlatformOptions } from '../../components/dashboard/composer/PlatformOptions';
import { PublishingSummary } from '../../components/dashboard/composer/PublishingSummary';
import { PublishingProgress } from '../../components/dashboard/composer/PublishingProgress';
import {
  DEFAULT_WORKSPACE_PANEL,
  WorkspaceTabs,
} from '../../components/dashboard/composer/WorkspaceTabs';
import type { WorkspacePanel } from '../../components/dashboard/composer/WorkspaceTabs';
import type { ScheduleMode } from '../../types/composer';

/** Width of the publish pane, shared by its header cell so the rule lines up. */
const PUBLISH_PANE = 'lg:w-[300px]';

export function ComposerPage() {
  const { t } = useTranslation();
  const mediaRef = useRef<MediaUploadHandle>(null);

  const [panel, setPanel] = useState<WorkspacePanel>(DEFAULT_WORKSPACE_PANEL);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [libraryCollapsed, setLibraryCollapsed] = useState(false);
  const [aiNoticeOpen, setAiNoticeOpen] = useState(false);
  const [publishMenuOpen, setPublishMenuOpen] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);

  const {
    state,
    accounts,
    accountsLoading,
    selectedPlatforms,
    validation,
    setText,
    addFiles,
    removeMedia,
    toggleLibraryFile,
    attachedLibraryIds,
    reorderMedia,
    toggleAccount,
    setSchedule,
    setPlatformOption,
    setYouTubeThumbnail,
    publish,
    retryAccount,
    saveAsDraft,
    closePublishProgress,
  } = useComposer();

  const isPublishing =
    state.publishPhase === 'validating' ||
    state.publishPhase === 'uploading_media' ||
    state.publishPhase === 'publishing';

  /** The picker lives in Compose, so reaching it has to switch tabs first. */
  const openMediaPicker = () => {
    setPanel('compose');
    mediaRef.current?.openPicker();
  };

  const handleSaveDraft = useCallback(async () => {
    setDraftSaving(true);
    try {
      await saveAsDraft();
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 2500);
    } finally {
      setDraftSaving(false);
    }
  }, [saveAsDraft]);

  usePageHeader({
    title: t('composer.pageTitle'),
    subtitle: t('composer.pageSubtitle'),
  });

  const publishLabel = (() => {
    if (isPublishing) return t('common.processing');
    if (state.schedule.mode === 'scheduled') return t('composer.publish.schedule');
    if (state.schedule.mode === 'draft') return t('composer.publish.draft');
    return t('composer.publish.now');
  })();

  const chooseMode = (mode: ScheduleMode) => {
    setSchedule({ ...state.schedule, mode });
    setPublishMenuOpen(false);
  };

  return (
    <div className="flex h-full flex-col gap-3 lg:flex-row">
      {/* ── Media library ───────────────────────────────────────────────────── */}
      <MediaLibraryTree
        collapsed={libraryCollapsed}
        onToggleCollapsed={() => setLibraryCollapsed((v) => !v)}
        attachedIds={attachedLibraryIds}
        onToggleFile={toggleLibraryFile}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
        {/* ── Destinations ──────────────────────────────────────────────────── */}
        <PlatformSelector
          accounts={accounts}
          loading={accountsLoading}
          selectedIds={state.selectedAccountIds}
          onToggle={toggleAccount}
        />

        {/* ── Workspace ─────────────────────────────────────────────────────── */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white">
          {/* Tab row. The last cell matches the publish pane width so the
            vertical rule below it continues the boundary between the two. */}
          <div className="flex shrink-0 border-b border-slate-200">
            <WorkspaceTabs
              tabs={[
                { panel: 'compose', label: t('composer.section.compose') },
                { panel: 'settings', label: t('composer.section.platformOptions') },
              ]}
              active={panel}
              onChange={setPanel}
            />

            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="flex shrink-0 items-center gap-1.5 border-l border-slate-200 bg-slate-50/80 px-4 py-2.5 text-[13px] font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-blue-700"
            >
              <Eye className="h-4 w-4" />
              {t('composer.section.preview')}
            </button>

            <div
              className={cn(
                'hidden shrink-0 border-l border-slate-200 px-4 py-2.5 text-[13px] font-bold text-slate-700 lg:block',
                PUBLISH_PANE
              )}
            >
              {t('composer.summary.title')}
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
            {/* ── Workspace ─────────────────────────────────────────────────── */}
            <div className="min-w-0 flex-1 overflow-y-auto p-4">
              {panel === 'compose' ? (
                <div className="space-y-4">
                  <PostEditor
                    text={state.text}
                    onChange={setText}
                    selectedPlatforms={selectedPlatforms}
                    onAiAssist={() => setAiNoticeOpen(true)}
                    onAttachMedia={openMediaPicker}
                  />
                  {/* The generation API does not exist yet. Saying so beats a
                    button that silently does nothing. */}
                  {aiNoticeOpen && (
                    <p className="flex items-start gap-1.5 rounded-lg bg-blue-50 px-3 py-2 text-[11.5px] leading-snug text-blue-800">
                      <Sparkles className="mt-px h-3.5 w-3.5 shrink-0" />
                      <span className="flex-1">{t('composer.aiComingSoon')}</span>
                      <button
                        type="button"
                        onClick={() => setAiNoticeOpen(false)}
                        aria-label={t('common.dismiss')}
                        className="shrink-0 text-blue-400 transition-colors hover:text-blue-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </p>
                  )}

                  <MediaUpload
                    ref={mediaRef}
                    media={state.media}
                    selectedPlatforms={selectedPlatforms}
                    onAddFiles={addFiles}
                    onRemove={removeMedia}
                    onReorder={reorderMedia}
                  />
                </div>
              ) : (
                <div>
                  {selectedPlatforms.length > 0 ? (
                    <PlatformOptions
                      selectedPlatforms={selectedPlatforms}
                      options={state.platformOptions}
                      onChange={setPlatformOption}
                      onYouTubeThumbnail={setYouTubeThumbnail}
                    />
                  ) : (
                    <p className="rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-[12px] text-slate-500">
                      {t('composer.options.selectPlatformFirst')}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* ── Publish pane ──────────────────────────────────────────────── */}
            <div
              className={cn(
                'flex shrink-0 flex-col border-t border-slate-200 lg:border-l lg:border-t-0',
                PUBLISH_PANE
              )}
            >
              <p className="border-b border-slate-200 px-4 py-2.5 text-[13px] font-bold text-slate-700 lg:hidden">
                {t('composer.summary.title')}
              </p>

              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
                <PublishingSummary
                  selectedPlatforms={selectedPlatforms}
                  text={state.text}
                  media={state.media}
                  schedule={state.schedule}
                  validation={validation}
                />

                <div className="border-t border-slate-200 pt-3">
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    {t('composer.section.schedule')}
                  </p>
                  <ScheduleSection schedule={state.schedule} onChange={setSchedule} />
                </div>
              </div>

              {/* Pinned: publishing is irreversible on several networks at once,
                  so its button must not be something you have to scroll to find. */}
              <div className="shrink-0 space-y-2 border-t border-slate-200 bg-slate-50/70 p-4">
                <button
                  type="button"
                  onClick={() => void handleSaveDraft()}
                  disabled={draftSaving || state.text.trim().length === 0}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-40"
                >
                  {draftSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {!draftSaving && draftSaved && <Save className="h-3.5 w-3.5 text-emerald-500" />}
                  {!draftSaving && !draftSaved && <FileText className="h-3.5 w-3.5" />}
                  {draftSaved ? t('composer.draftSaved') : t('composer.saveDraft')}
                </button>

                <div className="relative">
                  <div className="flex overflow-hidden rounded-lg bg-blue-600 shadow-sm transition-colors hover:bg-blue-700">
                    <button
                      type="button"
                      onClick={() => void publish()}
                      disabled={!validation.valid || isPublishing}
                      className="flex flex-1 items-center justify-center gap-1.5 px-3 py-2.5 text-[12.5px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isPublishing ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Send className="h-3.5 w-3.5" />
                      )}
                      {publishLabel}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPublishMenuOpen((v) => !v)}
                      aria-label={t('composer.publishOptions')}
                      aria-expanded={publishMenuOpen}
                      className="border-l border-white/25 px-2 text-white"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {publishMenuOpen && (
                    <div
                      role="menu"
                      className="absolute bottom-full right-0 z-20 mb-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg shadow-slate-900/10"
                    >
                      <MenuItem
                        active={state.schedule.mode === 'now'}
                        label={t('composer.publish.now')}
                        onClick={() => chooseMode('now')}
                      />
                      <MenuItem
                        active={state.schedule.mode === 'scheduled'}
                        label={t('composer.publish.schedule')}
                        onClick={() => chooseMode('scheduled')}
                      />
                      <MenuItem
                        active={state.schedule.mode === 'draft'}
                        label={t('composer.publish.draft')}
                        onClick={() => chooseMode('draft')}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        text={state.text}
        media={state.media}
        selectedPlatforms={selectedPlatforms}
        accounts={accounts}
      />

      <PublishingProgress
        phase={state.publishPhase}
        results={state.publishResults}
        onRetry={retryAccount}
        onClose={closePublishProgress}
      />
    </div>
  );
}

function MenuItem({
  active,
  label,
  onClick,
}: Readonly<{ active: boolean; label: string; onClick: () => void }>) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'w-full px-3 py-2 text-left text-[13px] font-medium transition-colors hover:bg-slate-50',
        active ? 'text-blue-700' : 'text-slate-700'
      )}
    >
      {label}
    </button>
  );
}
